import { TeamsFx } from '@microsoft/teamsfx';
import { GRAPH_SCOPES } from './utils/useGraph';
import { useTeamsFx } from './utils/useTeamsFx';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Provider, teamsTheme, Loader } from '@fluentui/react-northstar';
import { useAppDispatch, useAppSelector } from './hooks/store';
import 'antd/dist/antd.css';
import i18next from 'i18next';
import './locales/i18n';
import { I18nextProvider } from 'react-i18next';

import { fetchCurrentUser, signInUp } from './slices/auth';

import Privacy from './containers/pages/Privacy';
import TermsOfUse from './containers/pages/TermsOfUse';
import TabConfig from './containers/pages/TabConfig';
import Library from './containers/Library';
import ListArticles from './containers/ListArticles';
import Article from './containers/Article';
import AddArticleBot from './containers/AddArticleBot';

import AuthorizePermissions from './components/AuthorizePermissions';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

if (process.env.NODE_ENV !== 'development' && process.env.REACT_APP_SENTRY_DNS)
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DNS,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
  });

/**
 * The main app which handles the initialization and routing
 * of the app.
 */
export default function App() {
  const { theme, loading: teamsFxLoading, context } = useTeamsFx();
  const dispatch = useAppDispatch();
  const { isLogged } = useAppSelector(({ auth }) => auth);
  const [needPermissionAuthorize, setNeedPermissionAuthorize] = useState(false);

  useEffect(() => {
    if (!isLogged && context?.groupId) {
      signIn();
    }
  }, [isLogged, context?.groupId]);

  const signIn = async () => {
    const user = await dispatch(fetchCurrentUser()).unwrap();

    if (!user) {
      try {
        const teamsFx = new TeamsFx();
        const result = await teamsFx.getCredential().getToken(GRAPH_SCOPES);
        setNeedPermissionAuthorize(false);

        if (result?.token) {
          if (context?.groupId) {
            dispatch(
              signInUp({
                microsoftAccessToken: result.token,
                groupId: context?.groupId,
              }),
            );
          } else {
            console.error('[ERROR] context.groupId is undefined');
          }
        }
      } catch (error) {
        setNeedPermissionAuthorize(true);
      }
    }
  };

  return (
    <I18nextProvider i18n={i18next}>
      <Provider
        theme={theme || teamsTheme}
        styles={{ backgroundColor: '#eeeeee' }}
      >
        {needPermissionAuthorize ? (
          <AuthorizePermissions signIn={signIn} />
        ) : (
          <Router>
            {teamsFxLoading || !isLogged ? (
              <Loader style={{ margin: 100 }} />
            ) : (
              <Switch>
                <Route exact path="/library" component={Library} />
                <Route path="/tags/:tagId" component={ListArticles} />
                <Route path="/posts/:postId" component={Article} />
                <Route path="/bot-add-article" component={AddArticleBot} />
                <Route path="/privacy" component={Privacy} />
                <Route path="/termsofuse" component={TermsOfUse} />
                <Route path="/config" component={TabConfig} />
                <Route path="/" component={Library} />
              </Switch>
            )}
          </Router>
        )}
      </Provider>
    </I18nextProvider>
  );
}
